import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, CheckCircle2, Loader2, Trash2, BookOpen } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/utils/cn';
import { fdiToHaderup } from '@/utils/toothNotation';
import type { EnrichedPlanItem, PlanStatus } from '@/hooks/useTreatmentPlans';
import { useUpdatePlanStatus, useDeletePlanItem, useConvertPlanToTreatment } from '@/hooks/useTreatmentPlans';
import toast from 'react-hot-toast';

// ── Status helpers ─────────────────────────────────────────────────────────────

const STATUS_CYCLE: PlanStatus[] = ['proposed', 'accepted', 'scheduled'];

const STATUS_STYLES: Record<PlanStatus, string> = {
  proposed:
    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  accepted:
    'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
  scheduled:
    'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800',
  completed:
    'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
  cancelled:
    'bg-slate-50 text-slate-400 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface PlanItemRowProps {
  item: EnrichedPlanItem;
  patientId: string;
  clinicId: string;
  onEditRequest: (item: EnrichedPlanItem) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PlanItemRow({ item, patientId, clinicId, onEditRequest }: PlanItemRowProps) {
  const { t } = useTranslation();
  const [convertLoading, setConvertLoading] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const updateStatus = useUpdatePlanStatus();
  const deletePlan = useDeletePlanItem();
  const convertPlan = useConvertPlanToTreatment();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const cycleStatus = async () => {
    if (item.plan_status === 'completed' || item.plan_status === 'cancelled') return;
    const currentIdx = STATUS_CYCLE.indexOf(item.plan_status as PlanStatus);
    const nextStatus = STATUS_CYCLE[(currentIdx + 1) % STATUS_CYCLE.length];
    try {
      await updateStatus.mutateAsync({
        patientId,
        planId: item.id,
        status: nextStatus,
      });
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('dental.plan.confirmDelete', { defaultValue: 'Slet dette behandlingselement?' }))) return;
    try {
      await deletePlan.mutateAsync({ patientId, planId: item.id });
      toast.success(t('dental.plan.deleted', { defaultValue: 'Element slettet' }));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleMarkCompleted = async () => {
    setConvertLoading(true);
    try {
      await convertPlan.mutateAsync({ patientId, planId: item.id, clinicId });
      toast.success(t('dental.plan.actions.markCompleted'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    } finally {
      setConvertLoading(false);
    }
  };

  const haderup = fdiToHaderup(item.tooth_fdi);
  const isCompleted = item.plan_status === 'completed';
  const isCancelled = item.plan_status === 'cancelled';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-center gap-2 px-3 py-2 rounded-lg border transition-all',
        isDragging
          ? 'border-indigo-300 bg-indigo-50 shadow-lg dark:border-indigo-700 dark:bg-indigo-950/40'
          : 'border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-800/50',
        (isCompleted || isCancelled) && 'opacity-50',
      )}
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 flex-shrink-0"
        aria-label="Flyt behandlingselement"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Tooth FDI / Haderup */}
      <div className="w-16 flex-shrink-0">
        <span className="text-xs font-mono font-semibold text-slate-700 dark:text-slate-200">
          {item.tooth_fdi}
        </span>
        <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">
          ({haderup})
        </span>
      </div>

      {/* Procedure code + name */}
      <div className="flex-1 min-w-0">
        <span className="inline-flex items-center gap-1.5">
          <span className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-xs font-mono text-slate-600 dark:text-slate-300">
            {item.treatment_type}
          </span>
          <span className="text-sm text-slate-700 dark:text-slate-200 truncate">
            {item.procedure_name_da ?? item.treatment_description}
          </span>
        </span>
        {item.surfaces_treated && item.surfaces_treated.length > 0 && (
          <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
            ({item.surfaces_treated.join(', ')})
          </span>
        )}
      </div>

      {/* Performer */}
      <div className="w-24 flex-shrink-0 hidden lg:block">
        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {item.proposer_name ?? '—'}
        </span>
      </div>

      {/* Price */}
      <div className="w-20 flex-shrink-0 text-right">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {item.estimated_cost != null
            ? `${item.estimated_cost.toLocaleString('da-DK')} kr`
            : '—'}
        </span>
      </div>

      {/* Status chip — click to cycle */}
      <button
        type="button"
        onClick={cycleStatus}
        disabled={isCompleted || isCancelled || updateStatus.isPending}
        className={cn(
          'w-24 flex-shrink-0 rounded border px-2 py-0.5 text-center text-xs font-medium transition-colors',
          STATUS_STYLES[item.plan_status as PlanStatus] ?? STATUS_STYLES['proposed'],
          !isCompleted && !isCancelled && 'hover:opacity-80 cursor-pointer',
          (isCompleted || isCancelled) && 'cursor-default',
        )}
        title={!isCompleted && !isCancelled ? 'Klik for at avancere status' : undefined}
      >
        {t(`dental.plan.status.${item.plan_status}`, { defaultValue: item.plan_status })}
      </button>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        {!isCompleted && !isCancelled && (
          <>
            <button
              type="button"
              onClick={() => onEditRequest(item)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
              title={t('common.edit')}
            >
              <BookOpen className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={handleMarkCompleted}
              disabled={convertLoading}
              className="rounded p-1 text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-900/30 transition-colors"
              title={t('dental.plan.actions.markCompleted')}
            >
              {convertLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 transition-colors"
              title={t('common.delete')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Column header ─────────────────────────────────────────────────────────────

interface PlanItemRowHeaderProps {
  className?: string;
}

export function PlanItemRowHeader({ className }: PlanItemRowHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-400 dark:text-slate-500', className)}>
      <div className="w-4 flex-shrink-0" />
      <div className="w-16 flex-shrink-0">{t('dental.history.columns.tooth')}</div>
      <div className="flex-1 min-w-0">{t('dental.history.columns.code')}</div>
      <div className="w-24 flex-shrink-0 hidden lg:block">{t('dental.history.columns.performer')}</div>
      <div className="w-20 flex-shrink-0 text-right">{t('dental.history.columns.price')}</div>
      <div className="w-24 flex-shrink-0">Status</div>
      <div className="w-20 flex-shrink-0" />
    </div>
  );
}
